import { AbstractControl, FormArray, FormGroup } from '@angular/forms'
import { isDangerousKey, isPlainObject } from './json'

function assignPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.').filter(part => part.length > 0 && !isDangerousKey(part))
  if (parts.length === 0) {
    return
  }

  let cursor = target
  for (let index = 0; index < parts.length - 1; index++) {
    const key = parts[index]
    const next = cursor[key]
    if (!isPlainObject(next)) {
      cursor[key] = {}
    }
    cursor = cursor[key] as Record<string, unknown>
  }

  cursor[parts[parts.length - 1]] = value
}

function controlPath(parent: string, name: string): string {
  return parent ? `${parent}.${name}` : name
}

function visitControl(
  control: AbstractControl,
  path: string,
  patch: Record<string, unknown>,
  mapping?: Readonly<Record<string, string>>
): void {
  if (control.disabled) {
    return
  }

  if (control instanceof FormGroup) {
    for (const [name, child] of Object.entries(control.controls)) {
      visitControl(child, controlPath(path, name), patch, mapping)
    }
    return
  }

  if (control instanceof FormArray) {
    const arrayDirty =
      control.dirty || control.controls.some(child => child.dirty)
    if (arrayDirty && path) {
      const outputPath = mapping?.[path] ?? path
      assignPath(patch, outputPath, control.getRawValue())
      return
    }

    control.controls.forEach((child, index) => {
      visitControl(child, controlPath(path, String(index)), patch, mapping)
    })
    return
  }

  if (!control.dirty) {
    return
  }

  if (control.value === undefined) {
    return
  }

  const outputPath = mapping?.[path] ?? path
  assignPath(patch, outputPath, control.value === null ? null : control.value)
}

/**
 * Merge-patch fragment from dirty, enabled controls.
 * A dirty control whose value is `null` deletes that key. `undefined` is skipped.
 */
export function dirtyPatch(
  form: FormGroup,
  mapping?: Readonly<Record<string, string>>
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  visitControl(form, '', patch, mapping)
  return patch
}
